import { apiClient } from '@services/api';
import CircularProgress from '@material-ui/core/CircularProgress';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

// ─── Paleta ─────────────────────────────────────────────────────────────────

const C = {
  pageBg:       '#0a1628',
  navBg:        '#0f2040',
  card:         '#132035',
  cardInner:    '#0f1c2e',
  accent:       '#62DEC2',
  accentDim:    '#3aa38a',
  accentGold:   '#F5C842',
  danger:       '#e05252',
  label:        '#6e8eb0',
  value:        '#dce9fb',
  muted:        '#4a6380',
  sep:          '#1a3050',
  include:      '#2a5c42',
  notInclude:   '#5c2a2a',
  bubbleClient: '#1a3a5a',
  bubbleAgent:  '#1c3d2f',
  approveGrad:  'linear-gradient(135deg, #62DEC2 0%, #3aa38a 100%)',
  refuseGrad:   'linear-gradient(135deg, #e05252 0%, #b03030 100%)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (value, style = {}) => (
  <NumberFormat
    value={value ?? 0}
    displayType="text"
    decimalSeparator=","
    decimalScale={2}
    fixedDecimalScale
    thousandSeparator="."
    isNumericString
    prefix="R$ "
    renderText={(v) => <span style={style}>{v}</span>}
  />
);

const fmtDate = (str) => {
  if (!str) return '—';
  // Força horário local para evitar deslocamento de fuso (UTC vs local)
  try {
    const d = str.length === 10 ? new Date(str + 'T00:00:00') : new Date(str);
    return format(d, 'dd/MM/yyyy');
  } catch (e) { return str; }
};

const fmtDateTime = (str) => {
  if (!str) return '—';
  try { return format(new Date(str), 'dd/MM/yyyy HH:mm'); } catch (e) { return str; }
};

const isExpired = (exp) => exp && new Date(exp) < new Date();

const imgUrl = (path) => {
  const base = process.env.REACT_APP_BASE_IMAGE_URL;
  if (!path || !base || base === 'undefined') return null;
  return `${base}/${path}`;
};

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const Divider = () => (
  <div style={{ borderBottom: `1px solid ${C.sep}`, margin: '14px 0' }} />
);

const Label = ({ children }) => (
  <span style={{ color: C.label, fontSize: 12, display: 'block', marginBottom: 3 }}>
    {children}
  </span>
);

const Value = ({ children, style }) => (
  <span style={{ color: C.value, fontSize: 14, ...style }}>{children}</span>
);

const Row = ({ label, value, valueStyle, last }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: last ? 0 : 12,
    marginBottom: last ? 0 : 12,
    borderBottom: last ? 'none' : `1px solid ${C.sep}`,
    gap: 8,
  }}>
    <Label>{label}</Label>
    <span style={{ textAlign: 'right', color: C.value, ...valueStyle }}>{value}</span>
  </div>
);

const Badge = ({ color, bg, children }) => (
  <span style={{
    background: bg || color,
    color: '#fff',
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 700,
    display: 'inline-block',
    letterSpacing: 0.3,
  }}>
    {children}
  </span>
);

const SectionTitle = ({ children, icon }) => (
  <div style={{
    color: C.accent,
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  }}>
    {icon && <span>{icon}</span>}
    {children}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{
    background: C.card,
    borderRadius: 16,
    padding: '20px 20px',
    marginBottom: 16,
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    ...style,
  }}>
    {children}
  </div>
);

// ─── Seletor de data para mudança ────────────────────────────────────────────

const DateRequestSection = ({ tourId, tourDate: initialTourDate, presaleId, accessToken, budgetId, tourName }) => {
  const [open, setOpen]           = React.useState(true);
  const [availDates, setAvailDates] = React.useState(null);
  const [viewYear, setViewYear]   = React.useState(() => {
    if (initialTourDate) { try { return new Date(`${initialTourDate}T00:00:00`).getFullYear(); } catch (e) {} }
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    if (initialTourDate) { try { return new Date(`${initialTourDate}T00:00:00`).getMonth(); } catch (e) {} }
    return new Date().getMonth();
  });
  const [selDate, setSelDate]     = React.useState(null);
  const [saving, setSaving]       = React.useState(false);
  const [savedDate, setSavedDate] = React.useState(initialTourDate || null);

  React.useEffect(() => {
    if (!open || !tourId) return;
    setAvailDates(null);
    apiClient.httpInstance
      .get(`/app/tours/${tourId}/availability`)
      .then(r => {
        const map = {};
        (r.data?.dates || []).forEach(d => { map[d.date] = d.spots_left; });
        setAvailDates(map);
      })
      .catch(() => setAvailDates({}));
  }, [open, tourId]);

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pad = n => String(n).padStart(2, '0');
  const toKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

  const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const DAY_NAMES   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const handleDayClick = (key, available) => {
    if (!available) return;
    setSelDate(key);
  };

  const handleSave = () => {
    if (!selDate || saving) return;
    setSaving(true);
    apiClient.httpInstance
      .put(`/customer/presales/${presaleId}/budgets/${budgetId}/date?access_token=${accessToken}`, { date: selDate })
      .then(() => {
        window.location.reload();
      })
      .catch(() => setSaving(false));
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <span style={{ color: C.label, fontSize: 13, fontWeight: 600 }}>
          📅 {savedDate ? `Data preferida: ${fmtDate(savedDate)}` : 'Escolha uma data para este passeio'}
        </span>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'transparent', border: 'none', color: C.muted,
            fontSize: 12, cursor: 'pointer', padding: '2px 6px',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {open ? 'Fechar ▲' : 'Abrir ▼'}
        </button>
      </div>

      {open && (
        <div style={{
          background: C.cardInner,
          borderRadius: 12,
          padding: '16px 12px',
          border: `1px solid ${C.sep}`,
        }}>
          {/* Header do calendário */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: C.label, fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>‹</button>
            <span style={{ color: C.value, fontWeight: 700, fontSize: 14 }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: C.label, fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>›</button>
          </div>

          {/* Dias da semana */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ textAlign: 'center', color: C.muted, fontSize: 10, fontWeight: 700, padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Grid de dias */}
          {availDates === null ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: C.muted, fontSize: 13 }}>Verificando disponibilidade...</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                {/* Espaços vazios para alinhar o primeiro dia */}
                {Array.from({ length: firstDayOfMonth(viewYear, viewMonth) }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: daysInMonth(viewYear, viewMonth) }, (_, i) => i + 1).map(day => {
                  const key  = toKey(viewYear, viewMonth, day);
                  const date = new Date(viewYear, viewMonth, day);
                  const isPast = date < today;
                  const isAvail = !isPast && availDates[key] > 0;
                  const isSel  = selDate === key;
                  const isCurrent = savedDate && savedDate.startsWith(key);

                  return (
                    <button
                      key={day}
                      onClick={() => handleDayClick(key, isAvail)}
                      style={{
                        background: isSel ? C.accent : isCurrent ? '#1a3a5a' : isAvail ? '#1a3050' : 'transparent',
                        color: isSel ? C.pageBg : isPast ? C.muted : isAvail ? C.value : C.muted,
                        border: isCurrent ? `1px solid ${C.accent}` : '1px solid transparent',
                        borderRadius: 6,
                        padding: '6px 2px',
                        fontSize: 12,
                        fontWeight: isSel || isCurrent ? 700 : 400,
                        cursor: isAvail ? 'pointer' : 'default',
                        opacity: isPast ? 0.35 : 1,
                        textAlign: 'center',
                        WebkitTapHighlightColor: 'transparent',
                        textDecoration: !isAvail && !isPast && !isCurrent ? 'line-through' : 'none',
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Legenda */}
              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                  <span style={{ width: 10, height: 10, background: '#1a3050', borderRadius: 2, display: 'inline-block' }} /> Disponível
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                  <span style={{ width: 10, height: 10, background: 'transparent', borderRadius: 2, display: 'inline-block', textDecoration: 'line-through', fontSize: 10, color: C.muted }}>-</span> Esgotado
                </span>
                {savedDate && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
                    <span style={{ width: 10, height: 10, border: `1px solid ${C.accent}`, borderRadius: 2, display: 'inline-block' }} /> Data atual
                  </span>
                )}
              </div>

              {/* Nota */}
              <div style={{
                marginTop: 12,
                background: '#0f1c2e',
                borderRadius: 8,
                padding: '8px 12px',
                color: C.muted,
                fontSize: 12,
                lineHeight: 1.6,
                fontStyle: 'italic',
              }}>
                💬 Selecione um dia disponível para alterar sua preferência. A vaga só é confirmada quando a venda for finalizada.
              </div>

              {/* Botão salvar data */}
              {selDate && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    marginTop: 10,
                    width: '100%',
                    background: saving ? C.muted : C.accent,
                    color: C.pageBg,
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px 0',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: saving ? 'default' : 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {saving ? 'Salvando...' : `Confirmar data: ${fmtDate(selDate)}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Componente de item de tour ───────────────────────────────────────────────

const TourItemCard = ({ b, idx, presaleId, accessToken, isPaid }) => {
  const tour = b.travel?.[0] || {};
  // hasServiceId: true se o item tem um passeio/traslado associado
  const hasServiceId = !!(b.tour_id || b.translate_id || b.combo_id || b.travel_id || tour.id);
  const isTransfer   = b.travel_type === 'transfer' || b.type === 'transfer';

  const name        = tour.name || (isTransfer ? 'Transfer' : '—');
  const region = (tour.region_name || '').toLowerCase().trim();

  // Desconto automático por região e forma de pagamento
  const HIGH_REGIONS = ['santiago', 'atacama'];
  const isHighRegion = HIGH_REGIONS.includes(region);
  const pixPct  = isTransfer ? 0 : (isHighRegion ? 12 : 7);
  const cardPct = isTransfer ? 0 : (isHighRegion ? 10 : 5);

  // Dados do site já vêm embutidos no response do backend (PreSaleService busca server-to-server)
  const sitePhoto    = b.site_foto     || null;
  const siteDesc     = b.site_descricao || null;
  const siteIncluded = b.site_incluso  || null;
  const siteMonths   = Array.isArray(b.site_meses) ? b.site_meses.map(m => String(m).trim().toLowerCase()) : [];

  const rawDesc     = tour.description || '';
  const sysDesc     = rawDesc && rawDesc !== 'false' && rawDesc.length > 3 ? rawDesc : '';
  // Sistema tem prioridade; fallback para dados do site
  const description = sysDesc || siteDesc || '';
  const included    = tour.whats_included  || siteIncluded || '';
  const notIncluded = tour.whats_not_included || '';

  const adults    = parseInt(b.adults_total) || 0;
  const kids      = parseInt(b.kids_total)   || 0;

  // Subtotal correto: soma dos totais de adultos + crianças
  const adultsAmt  = Number(b.adults_total_amount || 0);
  const kidsAmt    = Number(b.kids_total_amount   || 0);
  const subtotal   = adultsAmt + kidsAmt || Number(b.price || 0);
  const discPct    = Number(b.discount_total_sale || 0);
  const discVal    = discPct > 0
    ? subtotal * discPct / 100
    : Number(b.discount_total_amount || 0);
  const total      = subtotal - discVal;

  // Preço unitário por adulto
  const priceAdult = b.travel_type === 'tour'
    ? Number(tour.price || 0)
    : (adults > 0 ? subtotal / adults : 0);
  const priceKid = Number(tour.kids_price || 0);

  // Transfer type badge
  const transferLabel = b.transfer_type === 'in' ? 'Transfer IN' :
                        b.transfer_type === 'out' ? 'Transfer OUT' :
                        b.transfer_type ? `Transfer ${b.transfer_type}` : null;

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      {/* Photo — sempre via API do site fulltour.com.br */}
      {hasServiceId && sitePhoto && (
        <div style={{ position: 'relative' }}>
          <img
            src={sitePhoto}
            alt={name}
            style={{
              width: '100%',
              height: 180,
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Gradient overlay on photo */}
          <div style={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            height: 80,
            background: `linear-gradient(to top, ${C.card}, transparent)`,
          }} />
          {transferLabel && (
            <div style={{ position: 'absolute', top: 12, right: 12 }}>
              <Badge bg="#4480A8">{transferLabel}</Badge>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: 20 }}>
        {/* Item header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <div style={{ color: C.accent, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 }}>
              Item {idx + 1}
            </div>
            <div style={{ color: C.value, fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
              {hasServiceId ? name : 'A confirmar com o consultor'}
            </div>
            {hasServiceId && !isTransfer && b.site_slug && (
              <a
                href={`https://fulltour.com.br/tour/${b.site_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.accent, fontSize: 11, fontWeight: 500, textDecoration: 'none', opacity: 0.85, marginTop: 2, display: 'inline-block' }}
              >
                Clique e saiba mais ↗
              </a>
            )}
            {region && (
              <div style={{ color: C.label, fontSize: 13, marginTop: 4 }}>
                📍 {region}
              </div>
            )}
          </div>
        </div>

        {/* Etiqueta tour 40 — Farellones */}
        {parseInt(b.travel_id) === 31 && (
          <div style={{
            background: 'linear-gradient(135deg, #0a3a2a 0%, #0d4a35 100%)',
            border: '2px solid #62DEC2', borderRadius: 14,
            padding: '16px 20px', marginTop: 12, marginBottom: 4,
            boxShadow: '0 0 18px rgba(98,222,194,0.18)',
          }}>
            <div style={{ color: '#62DEC2', fontWeight: 800, fontSize: 14, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center' }}>
              ⛷️ Ideal para passar o dia em Farellones
            </div>
            <div style={{ color: '#d1fae5', fontSize: 13, lineHeight: 1.8, textAlign: 'center' }}>
              Reserve o transporte e solicite o <strong>Day Use</strong> ao seu consultor.
            </div>
          </div>
        )}

        {!hasServiceId ? (
          <div style={{
            marginTop: 16,
            padding: '14px 16px',
            background: C.cardInner,
            borderRadius: 10,
            color: C.label,
            fontSize: 13,
            fontStyle: 'italic',
            lineHeight: 1.7,
            textAlign: 'center',
          }}>
            Os detalhes deste serviço serão informados pelo seu consultor. 💬
          </div>
        ) : (
          <>
            {/* Description — completa */}
            {description && (
              <div style={{ marginTop: 14 }}>
                <div style={{
                  padding: '12px 14px',
                  background: C.cardInner,
                  borderRadius: 10,
                  color: C.value,
                  fontSize: 13,
                  lineHeight: 1.7,
                }}>
                  {description}
                </div>
              </div>
            )}

            {/* Includes / Not includes */}
            {(included || notIncluded) && (
              <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                {included && (
                  <div style={{
                    flex: 1,
                    minWidth: 120,
                    background: C.include,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}>
                    <div style={{ color: '#62DEC2', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>✓ INCLUI</div>
                    {included.split('\n').map((line, i) => line.trim() && (
                      <div key={i} style={{ color: '#c4f0e4', fontSize: 12, lineHeight: 1.6 }}>• {line.trim()}</div>
                    ))}
                  </div>
                )}
                {notIncluded && (
                  <div style={{
                    flex: 1,
                    minWidth: 120,
                    background: C.notInclude,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}>
                    <div style={{ color: '#fc8181', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>✗ NÃO INCLUI</div>
                    {notIncluded.split('\n').map((line, i) => line.trim() && (
                      <div key={i} style={{ color: '#fcc', fontSize: 12, lineHeight: 1.6 }}>• {line.trim()}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Divider />

            {/* Date & passengers */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 100, background: C.cardInner, borderRadius: 10, padding: '10px 14px' }}>
                <Label>📅 Data</Label>
                {b.tour_date
                  ? <Value>{fmtDate(b.tour_date)}</Value>
                  : <span style={{ color: C.muted, fontSize: 13, fontStyle: 'italic' }}>A agendar</span>
                }
              </div>
              <div style={{ flex: 1, minWidth: 100, background: C.cardInner, borderRadius: 10, padding: '10px 14px' }}>
                <Label>👥 Passageiros</Label>
                <Value>{adults} adulto{adults !== 1 ? 's' : ''}{kids > 0 ? `, ${kids} criança${kids !== 1 ? 's' : ''}` : ''}</Value>
              </div>
            </div>

            {/* Date request / calendar — oculto após pagamento */}
            {!isTransfer && presaleId && !isPaid && (
              <DateRequestSection
                tourId={b.travel_id}
                tourDate={b.tour_date}
                budgetId={b.id}
                presaleId={presaleId}
                accessToken={accessToken}
                tourName={b.travel?.[0]?.name || 'Passeio'}
              />
            )}

            {/* Aviso: tours 58/139 mês vigente — apenas PIX */}
            {(b.travel_id === 58 || b.travel_id === 139) && (() => {
              if (!b.tour_date) return null;
              const d = new Date(b.tour_date + 'T00:00:00');
              if (d.getMonth() !== new Date().getMonth() || d.getFullYear() !== new Date().getFullYear()) return null;
              return (
                <div style={{
                  background: '#1a2a10', border: '1px solid #4a7c20', borderRadius: 10,
                  padding: '10px 14px', marginTop: 12,
                  color: '#a8e063', fontSize: 12, lineHeight: 1.6,
                }}>
                  ⚡ Passeio do mês vigente — pagamento exclusivo via <strong>PIX</strong>.
                </div>
              );
            })()}

            {/* Pricing */}
            <div style={{ background: C.cardInner, borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
              <div style={{ color: C.accent, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                Valores
              </div>

              {adults > 0 && (
                <Row
                  label={<span>{adults} adulto{adults !== 1 ? 's' : ''}<span style={{ color: C.muted, fontSize: 12 }}> × {fmtMoney(priceAdult)}</span></span>}
                  value={fmtMoney(adultsAmt || subtotal)}
                />
              )}
              {kids > 0 && (
                <Row
                  label={<span>{kids} criança{kids !== 1 ? 's' : ''}<span style={{ color: C.muted, fontSize: 12 }}> × {fmtMoney(priceKid)}</span></span>}
                  value={fmtMoney(kidsAmt)}
                />
              )}
              <Row label="Subtotal" value={fmtMoney(subtotal)} last={isTransfer} />

              {/* Desconto automático por forma de pagamento (só passeios) */}
              {!isTransfer && pixPct > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.sep}` }}>
                  <div style={{ color: C.label, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                    Desconto por pagamento
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#68D391', fontSize: 13 }}>⚡ PIX <span style={{ fontSize: 11, opacity: 0.8 }}>({pixPct}% desc.)</span></span>
                      <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>
                        {fmtMoney(subtotal * (1 - pixPct / 100))}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: C.accent, fontSize: 13 }}>💳 Cartão <span style={{ fontSize: 11, opacity: 0.8 }}>({cardPct}% desc.)</span></span>
                      <span style={{ color: C.accent, fontWeight: 700, fontSize: 14 }}>
                        {fmtMoney(subtotal * (1 - cardPct / 100))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

// ─── Comentários ─────────────────────────────────────────────────────────────

const CommentsSection = ({ saleId, token, comments, fetchComments, prefilledMessage, onPrefilledUsed, sectionRef, customerName }) => {
  const [newMessage, setNewMessage]   = useState('');

  // Quando chegar mensagem pré-preenchida (ex: solicitação de data), preenche o campo e rola até aqui
  useEffect(() => {
    if (prefilledMessage) {
      setNewMessage(prefilledMessage);
      sectionRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onPrefilledUsed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledMessage]);
  const [authorName, setAuthorName]   = useState(() => {
    // Usa o nome do cliente se disponível, senão busca localStorage
    if (customerName) return customerName;
    try { return localStorage.getItem('presale_author_name') || ''; } catch { return ''; }
  });
  const [askName, setAskName]         = useState(false);
  const [tempName, setTempName]       = useState('');
  const [sending, setSending]         = useState(false);
  const endRef = useRef(null);

  const prevCommentCount = useRef(0);
  useEffect(() => {
    if (comments.length > 0 && comments.length > prevCommentCount.current) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevCommentCount.current = comments.length;
  }, [comments]);

  const send = () => {
    const name = authorName.trim();
    if (!name) { setAskName(true); return; }
    if (!newMessage.trim()) return;
    setSending(true);
    apiClient.httpInstance
      .post(`/customer/presales/${saleId}/comments?access_token=${token}`, {
        message: newMessage.trim(),
        author_name: name,
      })
      .then(() => { setNewMessage(''); fetchComments(); })
      .catch(() => alert('Erro ao enviar. Tente novamente.'))
      .finally(() => setSending(false));
  };

  const confirmName = () => {
    const n = tempName.trim();
    if (!n) return;
    setAuthorName(n);
    try { localStorage.setItem('presale_author_name', n); } catch (e) {}
    setAskName(false);
    // Chama diretamente com o nome confirmado para evitar stale closure:
    // setTimeout(send, 50) usaria a versão antiga de send que fechou sobre
    // authorName vazio, porque o setState ainda não re-renderizou.
    if (!newMessage.trim()) return;
    setSending(true);
    apiClient.httpInstance
      .post(`/customer/presales/${saleId}/comments?access_token=${token}`, {
        message: newMessage.trim(),
        author_name: n,
      })
      .then(() => { setNewMessage(''); fetchComments(); })
      .catch(() => alert('Erro ao enviar. Tente novamente.'))
      .finally(() => setSending(false));
  };

  return (
    <div ref={sectionRef}>
    <Card>
      <SectionTitle icon="💬">Conversa com o consultor</SectionTitle>

      {/* Thread */}
      <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 14 }}>
        {comments.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0', fontStyle: 'italic' }}>
            Nenhuma mensagem ainda. Envie sua dúvida ou sugestão!
          </div>
        )}
        {comments.map((c) => {
          const isClient = c.author_type === 'customer';
          return (
            <div key={c.id} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isClient ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}>
              <div style={{
                background: isClient ? C.bubbleClient : C.bubbleAgent,
                borderRadius: isClient ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '10px 14px',
                maxWidth: '80%',
              }}>
                <div style={{ color: isClient ? C.accent : '#68D391', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                  {c.author_name}
                </div>
                <div style={{ color: C.value, fontSize: 14, lineHeight: 1.5 }}>{c.message}</div>
                <div style={{ color: C.muted, fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                  {fmtDateTime(c.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Ask name modal */}
      {askName && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ color: C.label, fontSize: 13, marginBottom: 8 }}>Qual é o seu nome?</p>
          <input
            type="text"
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            placeholder="Seu nome"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: C.cardInner, border: `1px solid ${C.sep}`,
              borderRadius: 8, color: C.value, fontSize: 16,
              padding: '10px 12px', marginBottom: 8,
            }}
            onKeyDown={(e) => e.key === 'Enter' && confirmName()}
          />
          <button
            onClick={confirmName}
            style={{
              background: C.accent, color: C.pageBg, border: 'none',
              borderRadius: 8, padding: '10px 24px', fontWeight: 700,
              fontSize: 14, cursor: 'pointer', width: '100%',
            }}
          >
            Confirmar e enviar
          </button>
        </div>
      )}

      {/* Input */}
      {!askName && (
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escreva sua mensagem..."
            rows={2}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            style={{
              flex: 1,
              background: C.cardInner,
              border: `1px solid ${C.sep}`,
              borderRadius: 10,
              color: C.value,
              fontSize: 15,
              padding: '10px 12px',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              width: '100%',
            }}
          />
          <button
            onClick={send}
            disabled={sending || !newMessage.trim()}
            style={{
              background: sending || !newMessage.trim() ? C.muted : C.accent,
              color: C.pageBg,
              border: 'none',
              borderRadius: 10,
              padding: '0 18px',
              fontWeight: 700,
              fontSize: 18,
              cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {sending ? '...' : '➤'}
          </button>
        </div>
      )}
    </Card>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────

// ─── Toast elegante ──────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  const isWarning = toast.type === 'warning';
  const icon     = isSuccess ? '✅' : isWarning ? '⚠️' : '❌';
  const color    = isSuccess ? '#68D391' : isWarning ? '#F6AD55' : '#fc8181';
  return (
    <div style={{
      position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, minWidth: 280, maxWidth: 360,
      background: isSuccess ? '#1a3d2a' : '#3d1a1a',
      border: `1px solid ${isSuccess ? '#68D391' : '#e05252'}`,
      borderRadius: 14, padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      animation: 'fadeInUp 0.25s ease',
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ color, fontSize: 14, fontWeight: 600 }}>
        {toast.msg}
      </span>
    </div>
  );
};

const PresalePage = () => {
  const { sale_id, token } = useParams();
  const [voucher, setVoucher]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [comments, setComments] = useState([]);
  const [actionLoading, setActionLoading]     = useState(false);
  const [showRefuse, setShowRefuse]           = useState(false);
  const [refuseReason, setRefuseReason]       = useState('');
  const [toast, setToast]                     = useState(null);
  const [chatPreFill, setChatPreFill]         = useState(null);
  const chatRef = useRef(null);

  // ── Pagamento ────────────────────────────────────────────────────────────
  const [showPayModal, setShowPayModal]       = useState(false);
  const [paySettings, setPaySettings]         = useState(null);   // {installments, item_discounts, totals}
  const [paySettingsLoading, setPaySettingsLoading] = useState(false);
  const pollRef                               = useRef(null);

  // Estados do PaymentModal levantados para o pai — evita re-mount ao re-renderizar PresalePage
  const [step, setStep]               = useState('method');
  const [method, setMethod]           = useState(null);
  const [payPct, setPayPct]           = useState(100);
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [installments, setInstallments] = useState(1);
  const [pixData, setPixData]         = useState(null);
  const [payLoading, setPayLoading]   = useState(false);
  const [cardForm, setCardForm]       = useState({
    holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '',
    postalCode: '', addressNumber: '',
  });

  // Reseta estados do modal ao abrir; cancela polling ao fechar
  useEffect(() => {
    if (showPayModal) {
      setStep('method'); setMethod(null); setPayPct(100);
      setAvailability(null); setAvailLoading(false);
      setInstallments(1); setPixData(null); setPayLoading(false);
      setCardForm({ holderName: '', number: '', expiryMonth: '', expiryYear: '', ccv: '', postalCode: '', addressNumber: '' });
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [showPayModal]);

  // ── CPF modal ────────────────────────────────────────────────────────────
  const [showCpfModal, setShowCpfModal] = useState(false);
  const [cpfInput, setCpfInput]         = useState('');
  const [cpfSaving, setCpfSaving]       = useState(false);

  // Reseta o .App que auth/app.css trava com position:fixed + overflow:hidden
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const appEl = document.querySelector('.App');
    if (appEl) {
      appEl.style.overflow = 'visible';
      appEl.style.position = 'relative';
      appEl.style.height   = 'auto';
    }
    document.body.style.height              = 'auto';
    document.body.style.overflow            = '';
    document.documentElement.style.height  = 'auto';
    document.documentElement.style.overflow = '';
    // scroll robusto — cobre window, body, html e iOS Safari
    const scrollTop = () => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      if (appEl) appEl.scrollTop = 0;
    };
    scrollTop();
    // segundo disparo após render completo (necessário em iOS)
    setTimeout(scrollTop, 100);
  }, []);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchVoucher = () => {
    setLoading(true);
    apiClient.httpInstance
      .get(`/customer/get_presale_voucher/${sale_id}?access_token=${token}`)
      .then((r) => {
        if (r.status === 200) {
          console.log('[presale] status=', r.data?.status, 'sda=', r.data?.status_discount_approve);
          setVoucher(r.data);
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
          setTimeout(() => { window.scrollTo(0, 0); document.body.scrollTop = 0; }, 150);
        }
      })
      .catch(() => setError('Não foi possível carregar o orçamento. Verifique o link ou tente novamente.'))
      .finally(() => setLoading(false));
  };

  const fetchComments = () => {
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/comments?access_token=${token}`)
      .then((r) => r.status === 200 && setComments(r.data || []))
      .catch(() => {});
  };

  // Carrega paySettings sempre que o voucher chega OU quando o status de aprovação muda
  // Não cacheia quando approval_status muda, pois o desconto gerencial pode ter sido aplicado
  useEffect(() => {
    if (!voucher) return;
    const exp    = voucher?.expires_at;
    const active = !(exp && new Date(exp) < new Date())
                   && voucher?.status !== 'canceled'
                   && voucher?.status !== 'sold';
    if (!active) return;
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/payment-settings?access_token=${token}`)
      .then((ps) => setPaySettings(ps.data))
      .catch((e) => console.error('[presale] paySettings error:', e?.response?.status, e?.response?.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucher?.approval_status, voucher?.status]);

  useEffect(() => {
    fetchVoucher();
    fetchComments();
    // Polling a cada 30s para detectar mudanças de status (aprovação gerencial, etc.)
    const intervalId = setInterval(() => {
      fetchVoucher();
      fetchComments();
    }, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale_id, token]);

  const handleApprove = () => {
    setActionLoading(true);
    apiClient.httpInstance
      .post(`/customer/presales/${sale_id}/approve?access_token=${token}`)
      .then(() => { window.location.reload(); })
      .catch((e) => showToast(e?.response?.data?.message || 'Erro ao aprovar. Tente novamente.'))
      .finally(() => setActionLoading(false));
  };

  const handleRefuse = () => {
    setActionLoading(true);
    apiClient.httpInstance
      .post(`/customer/presales/${sale_id}/refuse?access_token=${token}`, { reason: refuseReason })
      .then(() => { window.location.reload(); })
      .catch((e) => showToast(e?.response?.data?.message || 'Erro ao recusar. Tente novamente.'))
      .finally(() => setActionLoading(false));
  };

  const openPayModalAfterDoc = () => {
    setShowPayModal(true);
    // Sempre rebusca paySettings ao abrir o modal — garante que desconto gerencial
    // aprovado após o carregamento inicial da página seja refletido corretamente.
    setPaySettingsLoading(true);
    apiClient.httpInstance
      .get(`/customer/presales/${sale_id}/payment-settings?access_token=${token}`)
      .then((r) => setPaySettings(r.data))
      .catch(() => showToast('Erro ao carregar configurações de pagamento.'))
      .finally(() => setPaySettingsLoading(false));
  };

  const handleOpenPayModal = () => {
    // Valida que o documento é um CPF (11 dígitos) ou CNPJ (14 dígitos) real
    const rawDoc = String(voucher?.customer?.document ?? '').replace(/\D/g, '');
    const hasDoc = rawDoc.length === 11 || rawDoc.length === 14;
    if (!hasDoc) {
      setShowCpfModal(true);
      return;
    }
    openPayModalAfterDoc();
  };

  const handleSaveCpf = () => {
    const digits = cpfInput.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      showToast('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).');
      return;
    }
    setCpfSaving(true);
    apiClient.httpInstance
      .put(`/customer/presales/${sale_id}/update-document?access_token=${token}`, { document: digits })
      .then(() => {
        // atualiza o voucher local para não pedir de novo
        setVoucher(v => ({ ...v, customer: { ...(v?.customer || {}), document: digits } }));
        setShowCpfModal(false);
        openPayModalAfterDoc();
      })
      .catch((e) => showToast(e?.response?.data?.message || 'Erro ao salvar documento.'))
      .finally(() => setCpfSaving(false));
  };

  // ── Dados derivados ──────────────────────────────────────────────────────

  const logo          = voucher?.company?.logo ? imgUrl(voucher.company.logo) : null;
  const expired       = voucher ? isExpired(voucher.expires_at) : false;
  const customerStatus = voucher?.customer_status;
  const canAct        = !expired && customerStatus == null
                        && voucher?.status !== 'canceled'
                        && voucher?.status !== 'sold';

  const budgets = (voucher?.budgets || []).slice().sort((a, b) => {
    const ta = a.tour_date ? new Date(a.tour_date + 'T00:00:00').getTime() : Infinity;
    const tb = b.tour_date ? new Date(b.tour_date + 'T00:00:00').getTime() : Infinity;
    return ta - tb;
  });

  // Tours 58 (Alyan Sunset) e 139 (Monteluz): mês vigente → PIX exclusivo
  const _nowMonth = new Date().getMonth();
  const _nowYear  = new Date().getFullYear();
  const hasSpecialCurrentMonthTours = budgets.some(b => {
    if (b.travel_id !== 58 && b.travel_id !== 139) return false;
    if (!b.tour_date) return true; // sem data agendada: aplica restrição preventivamente
    const d = new Date(b.tour_date + 'T00:00:00');
    return d.getMonth() === _nowMonth && d.getFullYear() === _nowYear;
  });

  // Totais usando os mesmos campos dos cards individuais
  const HIGH_REGIONS_SUMMARY = ['santiago', 'atacama'];
  const totalBruto = budgets.reduce((s, b) => {
    const a = Number(b.adults_total_amount || 0);
    const k = Number(b.kids_total_amount   || 0);
    return s + (a + k || Number(b.price || 0));
  }, 0);
  const totalDesconto = budgets.reduce((s, b) => {
    const a   = Number(b.adults_total_amount || 0);
    const k   = Number(b.kids_total_amount   || 0);
    const sub = a + k || Number(b.price || 0);
    const pct = Number(b.discount_total_sale || 0);
    return s + (pct > 0 ? sub * pct / 100 : Number(b.discount_total_amount || 0));
  }, 0);
  const totalLiquido = totalBruto - totalDesconto;

  // Totais com desconto automático por pagamento
  const { totalPix: _localPixCalc, totalCard: _localCardCalc } = budgets.reduce((acc, b) => {
    const tour       = b.travel?.[0] || {};
    const isTransfer = b.travel_type === 'transfer' || b.type === 'transfer';
    const region     = (tour.region_name || '').toLowerCase().trim();
    const a   = Number(b.adults_total_amount || 0);
    const k   = Number(b.kids_total_amount   || 0);
    const sub = a + k || Number(b.price || 0);
    const high = HIGH_REGIONS_SUMMARY.includes(region);
    const pixPctB  = isTransfer ? 0 : (high ? 12 : 7);
    const cardPctB = isTransfer ? 0 : (high ? 10 : 5);
    acc.totalPix  += sub * (1 - pixPctB  / 100);
    acc.totalCard += sub * (1 - cardPctB / 100);
    return acc;
  }, { totalPix: 0, totalCard: 0 });
  const totalPixCalc  = paySettings?.totals?.pix  ?? _localPixCalc;
  const totalCardCalc = paySettings?.totals?.card ?? _localCardCalc;

  // Nome do cliente — testa cada candidato e usa o primeiro que não pareça telefone
  const isPhonelike = (str) => !str || /^[+\d\s()./-]+$/.test(str.trim());
  const pickName = (...candidates) => {
    for (const c of candidates) {
      if (c && !isPhonelike(c)) return c;
    }
    return null;
  };
  const clientName =
    pickName(
      voucher?.customer?.name,        // cliente vinculado (Customer model)
      voucher?.name,                   // nome editável do presale (campo principal)
      voucher?.customerData?.name,     // fallback legado
    ) ||
    voucher?.customerData?.email ||
    voucher?.email ||
    'Cliente';
  const firstName = clientName.split(/[\s@]/)[0];

  // ── PaymentModal ──────────────────────────────────────────────────────────

  const PaymentModal = () => {
    // estados vivem no pai (PresalePage) para evitar re-mount — ver declarações acima

    const cfg     = paySettings?.installments || {};
    const maxInst = cfg.max_installments || 12;
    const maxFree = cfg.max_no_interest_installments ?? 1;  // sem fallback fixo — usa exatamente o configurado
    const rate    = cfg.interest_rate || 1;
    const minVal  = cfg.min_installment_value || 5;
    const items   = paySettings?.item_discounts || [];
    const totals  = paySettings?.totals || {};

    const totalPix  = totals.pix  || 0;
    const totalCard = totals.card || 0;

    const calcInstallmentValue = (total, n) => {
      if (n <= maxFree) return total / n;
      return total * Math.pow(1 + rate / 100, n) / n;
    };

    const buildInstallments = (total) => {
      const opts = [];
      for (let n = 1; n <= maxInst; n++) {
        const val = calcInstallmentValue(total, n);
        if (val < minVal) break;
        opts.push({ n, val: round2(val), hasInterest: n > maxFree });
      }
      return opts;
    };

    const round2 = (v) => Math.round(v * 100) / 100;

    const fmtR = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const checkDates = () => {
      setAvailLoading(true);
      apiClient.httpInstance
        .get(`/customer/presales/${sale_id}/check-availability?access_token=${token}`)
        .then((r) => {
          setAvailability(r.data);
          setStep('dates');
        })
        .catch(() => showToast('Erro ao verificar disponibilidade.'))
        .finally(() => setAvailLoading(false));
    };

    const handleConfirmMethod = () => {
      if (!method) return;
      checkDates();
    };

    const handleProceedFromDates = () => {
      if (!availability?.all_ok) return;
      setStep(method === 'pix' ? 'pix' : 'card');
      if (method === 'pix') createPixCharge();
    };

    const createPixCharge = () => {
      setPayLoading(true);
      const pixAmount = round2(totalPix * payPct / 100);
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/pay?access_token=${token}`, {
          payment_method: 'pix',
          total: pixAmount,
          partial_pct: payPct,
          installments: 1,
        })
        .then((r) => {
          setPixData(r.data?.pix || null);
          setStep('pix');
          // Polling
          pollRef.current = setInterval(() => {
            apiClient.httpInstance
              .get(`/customer/presales/${sale_id}/pay/status?access_token=${token}`)
              .then((sr) => {
                if (sr.data?.paid) {
                  clearInterval(pollRef.current);
                  setStep('success');
                }
              })
              .catch(() => {});
          }, 5000);
        })
        .catch((e) => {
          showToast(e?.response?.data?.message || 'Erro ao gerar PIX.');
          setStep('method');
        })
        .finally(() => setPayLoading(false));
    };

    const handleCardPay = () => {
      if (!cardForm.holderName || !cardForm.number || !cardForm.expiryMonth || !cardForm.expiryYear || !cardForm.ccv) {
        showToast('Preencha todos os dados do cartão.'); return;
      }
      setPayLoading(true);
      const cardAmount = round2(totalCard * payPct / 100);
      apiClient.httpInstance
        .post(`/customer/presales/${sale_id}/pay?access_token=${token}`, {
          payment_method: 'credit_card',
          total: cardAmount,
          partial_pct: payPct,
          installments,
          card: {
            holderName: cardForm.holderName,
            number: cardForm.number.replace(/\D/g, ''),
            expiryMonth: cardForm.expiryMonth,
            expiryYear: cardForm.expiryYear,
            ccv: cardForm.ccv,
          },
          card_holder: {
            postalCode: cardForm.postalCode,
            addressNumber: cardForm.addressNumber,
          },
        })
        .then((r) => {
          if (r.data?.paid) setStep('success');
          else showToast('Pagamento não confirmado. Verifique os dados.');
        })
        .catch((e) => showToast(e?.response?.data?.message || 'Erro ao processar pagamento.'))
        .finally(() => setPayLoading(false));
    };


    const cardAmountForPct = round2(totalCard * payPct / 100);
    const installOpts = method === 'credit_card' ? buildInstallments(cardAmountForPct) : [];
    const bestFreeInst = installOpts.find(o => !o.hasInterest && o.n > 1) || installOpts[installOpts.length > 1 ? installOpts.filter(o => !o.hasInterest).length - 1 : 0];

    const inputStyle = {
      background: C.cardInner, border: `1px solid ${C.sep}`, borderRadius: 8,
      color: C.value, fontSize: 14, padding: '10px 12px', width: '100%',
      boxSizing: 'border-box', outline: 'none', marginBottom: 10,
    };

    if (!showPayModal) return null;

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(5,12,25,0.92)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '0',
      }} onClick={(e) => { if (e.target === e.currentTarget && step !== 'pix' && step !== 'success') setShowPayModal(false); }}>
        <div style={{
          background: C.card, borderRadius: '20px 20px 0 0',
          width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflowY: 'auto',
          padding: '24px 20px 32px',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ color: C.accent, fontWeight: 800, fontSize: 16 }}>
              {step === 'method'  && '💳 Forma de pagamento'}
              {step === 'dates'   && '📅 Verificação de datas'}
              {step === 'pix'     && '📱 Pagamento via PIX'}
              {step === 'card'    && '💳 Dados do cartão'}
              {step === 'success' && '✅ Pagamento confirmado!'}
            </span>
            {step !== 'success' && (
              <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setShowPayModal(false); }}
                style={{ background: 'transparent', border: 'none', color: C.label, fontSize: 22, cursor: 'pointer', padding: '0 4px' }}>×</button>
            )}
          </div>

          {paySettingsLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: C.label }}>
              <CircularProgress size={28} style={{ color: C.accent }} />
              <p style={{ marginTop: 12, fontSize: 13 }}>Carregando opções de pagamento...</p>
            </div>
          )}

          {/* ── STEP: method ── */}
          {!paySettingsLoading && step === 'method' && (
            <div>
              {/* Aviso: tours 57/124 mês vigente → apenas PIX */}
              {hasSpecialCurrentMonthTours && (
                <div style={{
                  background: '#1a2a10', border: '1px solid #4a7c20', borderRadius: 12,
                  padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#a8e063',
                  lineHeight: 1.6,
                }}>
                  ⚡ <strong>Alyan Sunset / Monteluz — mês vigente:</strong> este passeio é vendido exclusivamente via PIX para o mês atual. O pagamento por cartão não está disponível nesta reserva.
                </div>
              )}

              {/* Resumo por item */}
              {items.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {items.map((item) => (
                    <div key={item.budget_id} style={{
                      background: C.cardInner, borderRadius: 10, padding: '10px 14px', marginBottom: 8,
                      fontSize: 13, color: C.label,
                    }}>
                      <div style={{ color: C.value, fontWeight: 600, marginBottom: 4 }}>
                        {item.tour_name || (item.travel_type === 'transfer' ? 'Transfer' : 'Serviço')}
                        {item.region && <span style={{ color: C.muted, fontWeight: 400 }}> · {item.region}</span>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Subtotal: {fmtR(item.subtotal)}</span>
                        {item.pix_discount_pct > 0 && (
                          <span style={{ color: '#68D391' }}>
                            PIX {item.pix_discount_pct}% off · Card {item.card_discount_pct}% off
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Desconto especial — exibe somente se aprovado, sem revelar ao cliente que existe para outros */}
              {totals.manager_discount_val > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'rgba(104,211,145,0.08)', border: '1px solid rgba(104,211,145,0.25)',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 14,
                }}>
                  <span style={{ color: '#68D391', fontSize: 13, fontWeight: 700 }}>🎁 Desconto especial</span>
                  <span style={{ color: '#68D391', fontSize: 14, fontWeight: 800 }}>
                    − {fmtR(round2(totals.manager_discount_val))}
                  </span>
                </div>
              )}

              {/* Cards PIX / Cartão */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                {/* PIX */}
                <div onClick={() => setMethod('pix')} style={{
                  flex: 1, borderRadius: 14, padding: '16px 14px', cursor: 'pointer',
                  border: `2px solid ${method === 'pix' ? '#68D391' : C.sep}`,
                  background: method === 'pix' ? '#0d2a1e' : C.cardInner,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>⚡</div>
                  <div style={{ color: C.value, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>PIX</div>
                  <div style={{ color: '#68D391', fontWeight: 800, fontSize: 17 }}>
                    {fmtR(round2(totalPix * payPct / 100))}
                  </div>
                  <div style={{ color: C.label, fontSize: 11, marginTop: 4 }}>Pagamento instantâneo</div>
                  {totals.bruto > totalPix && (
                    <div style={{ color: '#68D391', fontSize: 11, marginTop: 2, fontWeight: 600 }}>
                      Economia de {fmtR(round2(totals.bruto - totalPix))}
                    </div>
                  )}
                </div>
                {/* Cartão — oculto quando tours especiais do mês vigente */}
                {!hasSpecialCurrentMonthTours && (
                  <div onClick={() => { setMethod('credit_card'); setPayPct(100); }} style={{
                    flex: 1, borderRadius: 14, padding: '16px 14px', cursor: 'pointer',
                    border: `2px solid ${method === 'credit_card' ? C.accent : C.sep}`,
                    background: method === 'credit_card' ? '#0d1e2a' : C.cardInner,
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>💳</div>
                    <div style={{ color: C.value, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Cartão</div>
                    <div style={{ color: C.accent, fontWeight: 800, fontSize: 17 }}>
                      {fmtR(round2(totalCard * payPct / 100))}
                    </div>
                    {installOpts.length > 0 && bestFreeInst && bestFreeInst.n > 1 && (
                      <div style={{ color: C.label, fontSize: 11, marginTop: 4 }}>
                        ou {bestFreeInst.n}x de {fmtR(bestFreeInst.val)} sem juros
                      </div>
                    )}
                    {totals.bruto > totalCard && (
                      <div style={{ color: C.accent, fontSize: 11, marginTop: 2, fontWeight: 600 }}>
                        Economia de {fmtR(round2(totals.bruto - totalCard))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Seleção de pagamento parcial — apenas PIX */}
              {method === 'pix' && <div style={{ marginBottom: 20 }}>
                <div style={{ color: C.label, fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Quanto deseja pagar agora?
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[30, 50, 100].map(pct => (
                    <div key={pct} onClick={() => setPayPct(pct)} style={{
                      flex: 1, borderRadius: 12, padding: '12px 8px', cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${payPct === pct ? '#68D391' : C.sep}`,
                      background: payPct === pct ? '#0d2a1e' : C.cardInner,
                      transition: 'all 0.15s',
                    }}>
                      <div style={{ color: payPct === pct ? '#68D391' : C.value, fontWeight: 800, fontSize: 15 }}>
                        {pct === 100 ? '100%' : `${pct}%`}
                      </div>
                      <div style={{ color: C.label, fontSize: 10, marginTop: 2 }}>
                        {pct === 100 ? 'Total' : 'Parcial'}
                      </div>
                    </div>
                  ))}
                </div>
                {payPct < 100 && (
                  <div style={{
                    background: '#2a1a0d', borderRadius: 10, padding: '10px 14px', marginTop: 10,
                    color: '#F6AD55', fontSize: 12, lineHeight: 1.6,
                  }}>
                    ⚠️ <strong>Pagamento parcial:</strong> o valor restante deverá ser pago até 48h antes do primeiro serviço, conforme contrato.
                  </div>
                )}
              </div>}

              <button onClick={handleConfirmMethod} disabled={!method || availLoading}
                style={{
                  width: '100%', background: method ? C.approveGrad : C.sep,
                  color: '#fff', border: 'none', borderRadius: 12,
                  padding: '16px 0', fontWeight: 800, fontSize: 15,
                  cursor: method ? 'pointer' : 'not-allowed',
                  opacity: availLoading ? 0.7 : 1,
                }}>
                {availLoading ? 'Verificando datas...' : 'Continuar →'}
              </button>
            </div>
          )}

          {/* ── STEP: dates ── */}
          {step === 'dates' && availability && (
            <div>
              <p style={{ color: C.label, fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
                Antes de confirmar o pagamento, verifique se todas as datas dos seus passeios têm vagas disponíveis:
              </p>
              <div style={{ marginBottom: 20 }}>
                {availability.items.map((item) => (
                  <div key={item.budget_id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 14px', borderRadius: 10, marginBottom: 8,
                    background: item.ok ? '#0d2a1e' : '#2a0d0d',
                    border: `1px solid ${item.ok ? '#2a5c42' : '#5c2a2a'}`,
                  }}>
                    <div>
                      <div style={{ color: C.value, fontWeight: 600, fontSize: 13 }}>{item.tour_name}</div>
                      <div style={{ color: C.label, fontSize: 12 }}>
                        {item.tour_date
                          ? new Date(item.tour_date + 'T00:00:00').toLocaleDateString('pt-BR')
                          : <span style={{ color: C.danger }}>Sem data selecionada</span>}
                        {item.tour_date && ` · ${item.needed} pessoa${item.needed !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {item.ok && <span style={{ color: '#68D391', fontWeight: 700, fontSize: 13 }}>✓ Disponível</span>}
                      {item.no_date && <span style={{ color: C.danger, fontWeight: 700, fontSize: 12 }}>Escolha uma data</span>}
                      {item.no_spots && !item.no_date && (
                        <div>
                          <span style={{ color: C.danger, fontWeight: 700, fontSize: 12 }}>Sem vagas</span>
                          <div style={{ color: C.label, fontSize: 11 }}>
                            {item.available != null ? `${item.available} disponíveis` : 'Indisponível'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {!availability.all_ok && (
                <div style={{
                  background: '#2a1a0d', borderRadius: 10, padding: '12px 14px',
                  color: '#F6AD55', fontSize: 13, marginBottom: 16, lineHeight: 1.5,
                }}>
                  ⚠️ Alguns passeios precisam de ajuste de data. Use o calendário em cada passeio (acima) para selecionar uma data disponível e volte aqui.
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep('method')} style={{
                  flex: 1, background: 'transparent', color: C.label,
                  border: `1px solid ${C.sep}`, borderRadius: 12,
                  padding: '14px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>← Voltar</button>
                <button
                  onClick={availability.all_ok ? handleProceedFromDates : () => setShowPayModal(false)}
                  style={{
                    flex: 2, background: availability.all_ok ? C.approveGrad : '#b45309',
                    color: '#fff', border: 'none', borderRadius: 12,
                    padding: '14px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                  }}>
                  {availability.all_ok ? 'Confirmar e pagar →' : '← Fechar e ajustar datas'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: pix ── */}
          {step === 'pix' && (
            <div style={{ textAlign: 'center' }}>
              {payLoading && (
                <div style={{ padding: '40px 0', color: C.label }}>
                  <CircularProgress size={28} style={{ color: C.accent }} />
                  <p style={{ marginTop: 12, fontSize: 13 }}>Gerando QR Code PIX...</p>
                </div>
              )}
              {!payLoading && pixData && (
                <div>
                  <div style={{
                    background: '#fff', borderRadius: 14, padding: 12,
                    display: 'inline-block', marginBottom: 16,
                  }}>
                    <img src={`data:image/png;base64,${pixData.qr_code_image}`}
                      alt="QR Code PIX"
                      style={{ width: 200, height: 200, display: 'block' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ color: C.label, fontSize: 12, marginBottom: 6 }}>Ou copie o código PIX:</div>
                    <div style={{
                      background: C.cardInner, borderRadius: 8, padding: '10px 12px',
                      fontSize: 11, color: C.value, wordBreak: 'break-all',
                      border: `1px solid ${C.sep}`, maxHeight: 60, overflow: 'hidden',
                    }}>{pixData.copy_paste}</div>
                    <button onClick={() => { navigator.clipboard?.writeText(pixData.copy_paste); showToast('Código copiado!', 'success'); }}
                      style={{
                        marginTop: 8, background: C.cardInner,
                        color: C.accent, border: `1px solid ${C.accent}`,
                        borderRadius: 8, padding: '8px 20px', fontSize: 13,
                        fontWeight: 700, cursor: 'pointer',
                      }}>📋 Copiar código</button>
                  </div>
                  <div style={{ color: '#68D391', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
                    {fmtR(round2(totalPix * payPct / 100))}
                    {payPct < 100 && <span style={{ color: C.label, fontSize: 13, fontWeight: 400 }}> ({payPct}% do total)</span>}
                  </div>
                  <div style={{ color: C.label, fontSize: 12, marginBottom: 20 }}>
                    Aguardando confirmação do pagamento...
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: C.muted, fontSize: 12 }}>
                    <CircularProgress size={12} style={{ color: C.muted }} />
                    Verificando automaticamente a cada 5 segundos
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP: card ── */}
          {step === 'card' && (
            <div>
              {/* Seletor de parcelas */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: C.label, fontSize: 12, display: 'block', marginBottom: 6 }}>Número de parcelas</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {installOpts.map((opt) => (
                    <div key={opt.n} onClick={() => setInstallments(opt.n)} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${installments === opt.n ? C.accent : C.sep}`,
                      background: installments === opt.n ? '#0d1e2a' : C.cardInner,
                    }}>
                      <span style={{ color: C.value, fontWeight: 600, fontSize: 14 }}>
                        {opt.n}x de {fmtR(opt.val)}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: opt.hasInterest ? '#F6AD55' : '#68D391',
                      }}>
                        {opt.hasInterest ? `com juros (${rate}%/mês)` : 'sem juros'}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ color: C.label, fontSize: 12, marginTop: 8, textAlign: 'right' }}>
                  Total: <strong style={{ color: C.value }}>{fmtR(round2(calcInstallmentValue(cardAmountForPct, installments) * installments))}</strong>
                  {payPct < 100 && <span style={{ color: C.label }}> ({payPct}% do valor)</span>}
                </div>
              </div>

              {/* Dados do cartão */}
              <div style={{ borderTop: `1px solid ${C.sep}`, paddingTop: 16, marginBottom: 16 }}>
                <div style={{ color: C.label, fontSize: 12, marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Dados do cartão
                </div>
                <input style={inputStyle} placeholder="Nome no cartão" value={cardForm.holderName}
                  onChange={(e) => setCardForm(f => ({ ...f, holderName: e.target.value }))} />
                <input style={inputStyle} placeholder="Número do cartão" value={cardForm.number}
                  onChange={(e) => setCardForm(f => ({ ...f, number: e.target.value.replace(/\D/g, '').slice(0, 16) }))}
                  inputMode="numeric" maxLength={16} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Mês (MM)" value={cardForm.expiryMonth}
                    onChange={(e) => setCardForm(f => ({ ...f, expiryMonth: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                    inputMode="numeric" maxLength={2} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Ano (AAAA)" value={cardForm.expiryYear}
                    onChange={(e) => setCardForm(f => ({ ...f, expiryYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    inputMode="numeric" maxLength={4} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="CVV" value={cardForm.ccv}
                    onChange={(e) => setCardForm(f => ({ ...f, ccv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    inputMode="numeric" maxLength={4} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="CEP do titular" value={cardForm.postalCode}
                    onChange={(e) => setCardForm(f => ({ ...f, postalCode: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                    inputMode="numeric" maxLength={8} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Nº endereço" value={cardForm.addressNumber}
                    onChange={(e) => setCardForm(f => ({ ...f, addressNumber: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep('method')} style={{
                  flex: 1, background: 'transparent', color: C.label,
                  border: `1px solid ${C.sep}`, borderRadius: 12,
                  padding: '14px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>← Voltar</button>
                <button onClick={handleCardPay} disabled={payLoading} style={{
                  flex: 2, background: C.approveGrad, color: '#fff',
                  border: 'none', borderRadius: 12,
                  padding: '14px 0', fontWeight: 800, fontSize: 14,
                  cursor: payLoading ? 'not-allowed' : 'pointer',
                  opacity: payLoading ? 0.7 : 1,
                }}>
                  {payLoading ? 'Processando...' : `Pagar ${fmtR(round2(calcInstallmentValue(cardAmountForPct, installments) * installments))}`}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: success ── */}
          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <div style={{ color: '#68D391', fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
                Pagamento confirmado!
              </div>
              <div style={{ color: C.label, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Sua reserva foi criada com sucesso. Em breve você receberá a confirmação e o voucher com todos os detalhes.
              </div>
              <button onClick={() => window.location.reload()} style={{
                background: C.approveGrad, color: '#fff',
                border: 'none', borderRadius: 12,
                padding: '16px 32px', fontWeight: 800, fontSize: 15,
                cursor: 'pointer',
              }}>Ver minha reserva</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ background: C.pageBg, minHeight: '100vh', fontFamily: '"Segoe UI", system-ui, sans-serif', color: C.value }}>
      <Toast toast={toast} />
      <PaymentModal />

      {/* ── CPF Modal ── */}
      {showCpfModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(5,12,25,0.93)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 16px',
        }} onClick={(e) => { if (e.target === e.currentTarget) setShowCpfModal(false); }}>
          <div style={{
            background: C.card, borderRadius: 20, width: '100%', maxWidth: 400,
            padding: '28px 22px 32px',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
          }}>
            <div style={{ color: C.accent, fontWeight: 800, fontSize: 17, marginBottom: 8 }}>
              📋 Documento necessário
            </div>
            <p style={{ color: C.label, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
              Para processar o pagamento precisamos do seu <strong style={{ color: C.value }}>CPF</strong> ou <strong style={{ color: C.value }}>CNPJ</strong>. Informe apenas os números.
            </p>
            <input
              type="text"
              inputMode="numeric"
              placeholder="CPF (11 dígitos) ou CNPJ (14 dígitos)"
              value={cpfInput}
              onChange={(e) => setCpfInput(e.target.value.replace(/\D/g, '').slice(0, 14))}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: C.cardInner, border: `1px solid ${C.sep}`,
                borderRadius: 10, color: C.value, fontSize: 15,
                padding: '12px 14px', outline: 'none', marginBottom: 14,
                letterSpacing: 2,
              }}
            />
            <button
              onClick={handleSaveCpf}
              disabled={cpfSaving || cpfInput.length < 11}
              style={{
                width: '100%', background: cpfInput.length >= 11 ? C.approveGrad : C.sep,
                color: '#fff', border: 'none', borderRadius: 12,
                padding: '15px 0', fontWeight: 800, fontSize: 15,
                cursor: cpfInput.length >= 11 && !cpfSaving ? 'pointer' : 'not-allowed',
                marginBottom: 10,
              }}
            >
              {cpfSaving ? 'Salvando...' : 'Confirmar e prosseguir para pagamento'}
            </button>
            <button
              onClick={() => setShowCpfModal(false)}
              style={{
                width: '100%', background: 'transparent',
                color: C.label, border: `1px solid ${C.sep}`,
                borderRadius: 12, padding: '12px 0',
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Nav bar */}
      <header style={{
        background: C.navBg,
        height: 58,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0, width: '100%', zIndex: 100,
        boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
        borderBottom: `1px solid ${C.sep}`,
      }}>
        {logo
          ? <img src={logo} alt="Full Tour" style={{ height: 36, maxWidth: 140, objectFit: 'contain' }} />
          : <span style={{ color: '#62DEC2', fontWeight: 800, fontSize: 22, letterSpacing: 2 }}>
              Full Tour
            </span>
        }
      </header>

      {/* Responsive grid styles + scroll fix */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        /* Desfaz o overflow:hidden + position:fixed que auth/app.css aplica globalmente */
        html, body { height: auto !important; overflow: auto !important; min-height: 100%; }
        .App { overflow: visible !important; position: relative !important; height: auto !important; }
        .presale-wrap { max-width: 1140px; margin: 0 auto; padding: 72px 14px 48px; }
        .presale-grid { display: flex; flex-direction: column; gap: 16px; }
        .presale-left { width: 100%; min-width: 0; }
        .presale-right { width: 100%; min-width: 0; }
        img { max-width: 100%; height: auto; }
        input, textarea, button { box-sizing: border-box; }
        @media (min-width: 769px) {
          .presale-grid { display: flex; flex-direction: row; gap: 22px; align-items: flex-start; margin-top: 16px; }
          .presale-left { flex: 1; }
          .presale-right { width: 360px; flex-shrink: 0; position: sticky; top: 74px; max-height: calc(100vh - 90px); overflow-y: auto; margin-top: 0; }
          .presale-right::-webkit-scrollbar { width: 4px; }
          .presale-right::-webkit-scrollbar-thumb { background: #1a3050; border-radius: 2px; }
        }
      `}</style>

      {/* Main */}
      <div className="presale-wrap">

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 80, gap: 16 }}>
            <CircularProgress style={{ color: C.accent }} size={40} />
            <span style={{ color: C.label, fontSize: 14 }}>Carregando seu orçamento...</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{
            background: C.card, borderRadius: 16, padding: 28,
            textAlign: 'center', color: C.danger, marginTop: 20,
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
            <div style={{ fontSize: 15, lineHeight: 1.6 }}>{error}</div>
          </div>
        )}

        {/* Content */}
        {!loading && !error && voucher && (
          <>
            {/* ── HERO: saudação personalizada (full width) ── */}
            <div style={{
              background: `linear-gradient(135deg, #0f2a45 0%, #132035 100%)`,
              borderRadius: 20,
              padding: '24px 22px',
              border: `1px solid ${C.sep}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>✈️</div>
                  <div style={{ color: C.value, fontSize: 20, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>
                    Olá, {firstName}!
                  </div>
                  <div style={{ color: C.label, fontSize: 14, lineHeight: 1.6 }}>
                    Preparamos este orçamento exclusivamente para você.
                    Confira os passeios e, quando estiver pronto, é só aprovar!
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 }}>
                  <div style={{
                    background: C.cardInner, borderRadius: 10, padding: '8px 14px',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <span style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Referência</span>
                    <span style={{ color: C.accent, fontSize: 15, fontWeight: 700 }}>#{voucher.references || voucher.id}</span>
                  </div>
                  <div style={{
                    background: C.cardInner, borderRadius: 10, padding: '8px 14px',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    <span style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Válido até</span>
                    <span style={{ color: expired ? C.danger : C.accentGold, fontSize: 15, fontWeight: 700 }}>
                      {fmtDate(voucher.expires_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status do cliente */}
              {(() => {
                const isCanceled = voucher?.status === 'canceled'
                  || voucher?.status_discount_approve === 'canceled';
                return (
                  <div style={{ marginTop: 14 }}>
                    {isCanceled && <Badge bg={C.danger}>⊘ Orçamento cancelado</Badge>}
                    {!isCanceled && customerStatus === 'approved' && <Badge bg="#276749">✓ Orçamento aprovado por você</Badge>}
                    {!isCanceled && customerStatus === 'refused'  && <Badge bg={C.danger}>✗ Orçamento recusado</Badge>}
                    {!isCanceled && customerStatus == null && !expired && <Badge bg="#1e3a5a">⏳ Aguardando sua resposta</Badge>}
                    {!isCanceled && customerStatus == null && expired  && <Badge bg={C.danger}>⏰ Orçamento expirado</Badge>}
                  </div>
                );
              })()}

              {customerStatus === 'refused' && voucher.customer_refuse_reason && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#3d1a1a', borderRadius: 10 }}>
                  <Label>Motivo da recusa:</Label>
                  <Value style={{ fontSize: 13 }}>{voucher.customer_refuse_reason}</Value>
                </div>
              )}
            </div>

            {/* ── GRID DUAS COLUNAS ── */}
            <div className="presale-grid">

              {/* ── COLUNA ESQUERDA: itens do orçamento ── */}
              <div className="presale-left">
                {budgets.length > 0 && (
                  <div>
                    <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>
                      {budgets.length} {budgets.length === 1 ? 'serviço' : 'serviços'} no orçamento
                    </div>
                    {budgets.map((b, idx) => (
                      <TourItemCard key={idx} b={b} idx={idx} presaleId={sale_id} accessToken={token} isPaid={voucher?.payment_status === 'paid'} />
                    ))}
                  </div>
                )}
              </div>

              {/* ── COLUNA DIREITA: resumo + ações ── */}
              <div className="presale-right">

                {/* Resumo total — dois cards PIX / Cartão */}
                {canAct && totalPixCalc > 0 ? (() => {
                  const pixDiscount  = Math.round((totalBruto - totalPixCalc)  * 100) / 100;
                  const cardDiscount = Math.round((totalBruto - totalCardCalc) * 100) / 100;
                  const pixTotal     = Math.round(totalPixCalc  * 100) / 100;
                  const cardTotal    = Math.round(totalCardCalc * 100) / 100;
                  const maxFree      = paySettings?.installments?.max_no_interest_installments ?? null;
                  const instVal      = maxFree ? Math.round(cardTotal / maxFree * 100) / 100 : 0;
                  const rowStyle     = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 };
                  const labelStyle   = { color: C.label, fontSize: 13 };

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                      {/* ── PIX ── */}
                      <div style={{
                        background: 'linear-gradient(135deg, #082a18 0%, #0e3320 100%)',
                        border: '1px solid rgba(104,211,145,0.25)',
                        borderRadius: 14, padding: '16px 18px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: 20 }}>⚡</span>
                          <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}>PIX</span>
                        </div>

                        <div style={rowStyle}>
                          <span style={labelStyle}>Valor total</span>
                          <span style={{ color: C.value, fontSize: 14 }}>{fmtMoney(totalBruto)}</span>
                        </div>
                        {pixDiscount > 0 && (
                          <div style={rowStyle}>
                            <span style={labelStyle}>Você economiza</span>
                            <span style={{ color: '#68D391', fontWeight: 600, fontSize: 14 }}>− {fmtMoney(pixDiscount)}</span>
                          </div>
                        )}

                        <div style={{
                          borderTop: '1px solid rgba(104,211,145,0.2)',
                          marginTop: 10, paddingTop: 12,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <span style={{ color: '#68D391', fontWeight: 700, fontSize: 13 }}>Total a pagar</span>
                          <span style={{ color: '#68D391', fontWeight: 900, fontSize: 22 }}>{fmtMoney(pixTotal)}</span>
                        </div>
                      </div>

                      {/* ── Cartão ── */}
                      <div style={{
                        background: 'linear-gradient(135deg, #0d1e33 0%, #132035 100%)',
                        border: `1px solid ${C.sep}`,
                        borderRadius: 14, padding: '16px 18px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                          <span style={{ fontSize: 20 }}>💳</span>
                          <span style={{ color: C.accent, fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}>Cartão de crédito</span>
                        </div>

                        <div style={rowStyle}>
                          <span style={labelStyle}>Valor total</span>
                          <span style={{ color: C.value, fontSize: 14 }}>{fmtMoney(totalBruto)}</span>
                        </div>
                        {cardDiscount > 0 && (
                          <div style={rowStyle}>
                            <span style={labelStyle}>Desconto</span>
                            <span style={{ color: C.accent, fontWeight: 600, fontSize: 14 }}>− {fmtMoney(cardDiscount)}</span>
                          </div>
                        )}

                        <div style={{
                          borderTop: `1px solid ${C.sep}`,
                          marginTop: 10, paddingTop: 12,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: C.accent, fontWeight: 700, fontSize: 13 }}>Total a pagar</span>
                            <span style={{ color: C.accent, fontWeight: 900, fontSize: 22 }}>{fmtMoney(cardTotal)}</span>
                          </div>
                          {maxFree > 1 && (
                            <div style={{ color: C.label, fontSize: 12, marginTop: 5, textAlign: 'right' }}>
                              ou {maxFree}× sem juros de {fmtMoney(instVal)}
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  );
                })() : (() => {
                  const isPaid    = voucher?.payment_status === 'paid';
                  const isPix     = voucher?.payment_method === 'pix';
                  const instCount = parseInt(voucher?.payment_installments || voucher?.installments) || 1;
                  const paidBase  = isPix
                    ? Math.round(totalPixCalc  * 100) / 100
                    : Math.round(totalCardCalc * 100) / 100;
                  const discount  = Math.round((totalBruto - paidBase) * 100) / 100;
                  const rowStyle  = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 };

                  if (isPaid) {
                    return (
                      <Card style={{ background: 'linear-gradient(135deg, #0f2a45 0%, #132035 100%)', border: `1px solid ${C.sep}` }}>
                        <SectionTitle icon="💰">Resumo do pagamento</SectionTitle>
                        <div style={rowStyle}>
                          <span style={{ color: C.label, fontSize: 13 }}>{budgets.length} serviço{budgets.length !== 1 ? 's' : ''}</span>
                          <span style={{ color: C.value, fontSize: 13 }}>{fmtMoney(totalBruto)}</span>
                        </div>
                        {discount > 0 && (
                          <div style={rowStyle}>
                            <span style={{ color: C.label, fontSize: 13 }}>Desconto {isPix ? 'PIX' : 'cartão'}</span>
                            <span style={{ color: '#68D391', fontWeight: 600, fontSize: 13 }}>− {fmtMoney(discount)}</span>
                          </div>
                        )}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          paddingTop: 12, borderTop: '1px solid rgba(104,211,145,0.3)', marginTop: 8,
                        }}>
                          <span style={{ color: '#68D391', fontWeight: 700, fontSize: 14 }}>✅ Valor pago</span>
                          <span style={{ color: '#68D391', fontWeight: 900, fontSize: 22 }}>{fmtMoney(paidBase)}</span>
                        </div>
                        <div style={{ color: C.label, fontSize: 12, textAlign: 'right', marginTop: 6 }}>
                          {isPix
                            ? '⚡ Pago via PIX'
                            : (() => {
                                const cfg      = paySettings?.installments || {};
                                const maxFree  = cfg.max_no_interest_installments ?? 1;
                                const rate     = cfg.interest_rate || 1;
                                const hasInterest = instCount > maxFree;
                                const instVal  = hasInterest
                                  ? paidBase * Math.pow(1 + rate / 100, instCount) / instCount
                                  : paidBase / instCount;
                                const totalFinal = hasInterest
                                  ? Math.round(instVal * instCount * 100) / 100
                                  : paidBase;
                                const fmtInst  = (v) => fmtMoney(Math.round(v * 100) / 100);
                                if (instCount <= 1) return '💳 Cartão — 1× sem juros';
                                return hasInterest
                                  ? `💳 Cartão — ${instCount}× de ${fmtInst(instVal)} (juros da operadora) · Total ${fmtInst(totalFinal)}`
                                  : `💳 Cartão — ${instCount}× de ${fmtInst(instVal)} sem juros`;
                              })()}
                        </div>
                      </Card>
                    );
                  }

                  return (() => {
                    const approvalStatus = voucher?.status_discount_approve;
                    const approvalType   = voucher?.approval_type;
                    const approvalVal    = Number(voucher?.approval_manager_value || 0);
                    const hasManagerDiscount = approvalStatus === 'approved'
                      && approvalVal > 0
                      && approvalType !== 'installment';
                    const hasRegionDiscount  = totalDesconto > 0;

                    // Desconto gerencial é calculado sobre o liquido (após desconto regional)
                    let managerDiscountAmt = 0;
                    if (hasManagerDiscount) {
                      if (approvalType === 'discount_pct') {
                        managerDiscountAmt = Math.round(totalLiquido * approvalVal / 100 * 100) / 100;
                      } else {
                        managerDiscountAmt = approvalVal;
                      }
                    }
                    const totalFinal = Math.round((totalLiquido - managerDiscountAmt) * 100) / 100;
                    const rowStyle   = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 };

                    return (
                      <Card style={{ background: 'linear-gradient(135deg, #0f2a45 0%, #132035 100%)', border: `1px solid ${C.sep}` }}>
                        <SectionTitle icon="💰">Resumo do orçamento</SectionTitle>
                        {/* Subtotal bruto */}
                        <div style={rowStyle}>
                          <span style={{ color: C.label, fontSize: 13 }}>{budgets.length} serviço{budgets.length !== 1 ? 's' : ''}</span>
                          <span style={{ color: C.value, fontSize: 13 }}>{fmtMoney(totalBruto)}</span>
                        </div>
                        {/* Desconto regional */}
                        {hasRegionDiscount && (
                          <div style={rowStyle}>
                            <span style={{ color: '#68D391', fontSize: 13 }}>Desconto de região</span>
                            <span style={{ color: '#68D391', fontWeight: 600, fontSize: 13 }}>− {fmtMoney(totalDesconto)}</span>
                          </div>
                        )}
                        {/* Desconto gerencial */}
                        {hasManagerDiscount && (
                          <div style={rowStyle}>
                            <span style={{ color: '#F6E05E', fontSize: 13 }}>
                              {approvalType === 'discount_pct'
                                ? `Desconto gerencial (${approvalVal}%)`
                                : 'Desconto gerencial'}
                            </span>
                            <span style={{ color: '#F6E05E', fontWeight: 600, fontSize: 13 }}>− {fmtMoney(managerDiscountAmt)}</span>
                          </div>
                        )}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          paddingTop: 14, borderTop: `2px solid ${C.sep}`, marginTop: 8,
                        }}>
                          <span style={{ color: C.value, fontWeight: 700, fontSize: 14 }}>Total a pagar</span>
                          <span style={{ color: C.value, fontWeight: 900, fontSize: 20 }}>{fmtMoney(totalFinal)}</span>
                        </div>
                      </Card>
                    );
                  })();
                })()}

                {/* Validade */}
                {!expired && customerStatus == null && (
                  <div style={{
                    textAlign: 'center',
                    padding: '12px 16px',
                    background: '#1a2d1a',
                    borderRadius: 12,
                    marginBottom: 16,
                    color: C.accentGold,
                    fontSize: 13,
                    fontWeight: 600,
                  }}>
                    ⏱️ Valores válidos até {fmtDate(voucher.expires_at)} · Reserve agora e garanta esses valores!
                  </div>
                )}

                {/* Ação: aprovar / recusar */}
                {canAct && (
                  <Card>
                    <SectionTitle>Sua resposta</SectionTitle>

                    {!showRefuse ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <button
                          onClick={handleOpenPayModal}
                          disabled={actionLoading}
                          style={{
                            width: '100%',
                            background: C.approveGrad,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            padding: '16px 12px',
                            fontWeight: 800,
                            fontSize: 15,
                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                            opacity: actionLoading ? 0.7 : 1,
                            minHeight: 52,
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: 0.3,
                          }}
                        >
                          ✓ Aprovar e pagar
                        </button>
                        <button
                          onClick={() => setShowRefuse(true)}
                          disabled={actionLoading}
                          style={{
                            width: '100%',
                            background: 'transparent',
                            color: C.danger,
                            border: `2px solid ${C.danger}`,
                            borderRadius: 12,
                            padding: '14px 12px',
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                            opacity: actionLoading ? 0.7 : 1,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          Recusar
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p style={{ color: C.label, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
                          Nos conte o motivo (opcional). Sua opinião nos ajuda a melhorar!
                        </p>
                        <textarea
                          value={refuseReason}
                          onChange={(e) => setRefuseReason(e.target.value)}
                          rows={3}
                          placeholder="Ex: preço fora do orçamento, datas indisponíveis..."
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            background: C.cardInner,
                            border: `1px solid ${C.sep}`,
                            borderRadius: 10,
                            color: C.value,
                            fontSize: 15,
                            padding: '12px 14px',
                            resize: 'vertical',
                            marginBottom: 12,
                            outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button
                            onClick={handleRefuse}
                            disabled={actionLoading}
                            style={{
                              flex: 1,
                              background: C.refuseGrad,
                              color: '#fff',
                              border: 'none',
                              borderRadius: 10,
                              padding: '14px 0',
                              fontWeight: 700,
                              fontSize: 14,
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              opacity: actionLoading ? 0.7 : 1,
                              minHeight: 48,
                            }}
                          >
                            {actionLoading ? '...' : 'Confirmar recusa'}
                          </button>
                          <button
                            onClick={() => setShowRefuse(false)}
                            style={{
                              flex: 1,
                              background: 'transparent',
                              color: C.label,
                              border: `1px solid ${C.sep}`,
                              borderRadius: 10,
                              padding: '14px 0',
                              fontWeight: 600,
                              fontSize: 14,
                              cursor: 'pointer',
                              minHeight: 48,
                            }}
                          >
                            Voltar
                          </button>
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {/* Pagamento realizado */}
                {voucher?.payment_status === 'paid' && (
                  <div style={{
                    textAlign: 'center', padding: '20px 16px',
                    background: '#0a2d2d', border: '1px solid #14b8a6',
                    borderRadius: 16, marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                    <div style={{ color: '#2dd4bf', fontWeight: 700, fontSize: 16 }}>Pagamento recebido!</div>
                    <div style={{ color: C.label, fontSize: 13, marginTop: 6 }}>
                      Nosso consultor entrará em contato para finalizar a sua reserva.
                    </div>
                  </div>
                )}

                {/* Resposta já dada (aprovado mas ainda não pagou) */}
                {customerStatus === 'approved' && voucher?.payment_status !== 'paid' && (
                  <div style={{
                    textAlign: 'center', padding: '20px 16px',
                    background: '#1a3d2a', borderRadius: 16, marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                    <div style={{ color: '#68D391', fontWeight: 700, fontSize: 16 }}>Orçamento aprovado!</div>
                    <div style={{ color: C.label, fontSize: 13, marginTop: 6 }}>
                      Nossa equipe entrará em contato em breve para finalizar sua reserva.
                    </div>
                  </div>
                )}

                {/* Botão de pagamento para cliente que já aprovou mas ainda não pagou */}
                {!canAct && customerStatus === 'approved' &&
                 voucher?.payment_status !== 'paid' &&
                 voucher?.payment_status !== 'partial' && (
                  <button
                    onClick={handleOpenPayModal}
                    style={{
                      width: '100%',
                      background: C.approveGrad,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      padding: '16px 12px',
                      fontWeight: 800,
                      fontSize: 15,
                      cursor: 'pointer',
                      marginBottom: 16,
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: 0.3,
                    }}
                  >
                    💳 Realizar pagamento
                  </button>
                )}

                {/* Comentários */}
                <CommentsSection
                  saleId={sale_id}
                  token={token}
                  comments={comments}
                  fetchComments={fetchComments}
                  prefilledMessage={chatPreFill}
                  onPrefilledUsed={() => setChatPreFill(null)}
                  sectionRef={chatRef}
                  customerName={clientName !== 'Cliente' ? clientName : undefined}
                />
              </div>
              {/* fim col-right */}
            </div>
            {/* fim grid */}

            {/* ── RODAPÉ FULL TOUR (full width) ── */}
            <Card style={{
              background: 'linear-gradient(135deg, #0f2040 0%, #0a1628 100%)',
              border: `1px solid ${C.sep}`,
              textAlign: 'center',
              marginTop: 0,
            }}>
              {/* Logo / marca */}
              <div style={{ marginBottom: 12 }}>
                {logo
                  ? <img src={logo} alt="Full Tour" style={{ height: 44, maxWidth: 160, objectFit: 'contain' }} />
                  : <span style={{ color: '#62DEC2', fontWeight: 900, fontSize: 28, letterSpacing: 2 }}>Full Tour</span>
                }
              </div>

              <div style={{ color: C.label, fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
                Somos especialistas em turismo de experiência em Santiago, Buenos Aires e destinos da América do Sul.
                Cada viagem é planejada com cuidado e atenção para que você viva momentos inesquecíveis.
              </div>

              <Divider />

              {/* Apps */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: C.label, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 12 }}>
                  Baixe o FT App
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {/* iOS */}
                  <a
                    href="https://apps.apple.com/ar/app/full-tour/id6765520551"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: '#000', borderRadius: 10,
                      padding: '9px 18px', border: '1px solid #444',
                      minWidth: 145,
                    }}>
                      <svg width="22" height="22" viewBox="0 0 814 1000" fill="white" xmlns="http://www.w3.org/2000/svg">
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.2 842.1 0 727.3 0 617.8 0 405.4 140.5 280.7 279.4 280.7c70.6 0 129.2 46.4 173.1 46.4 42.1 0 108.6-49 191.2-49 30.8 0 110.8 2.9 168.3 73.4zm-234.6-194c30.1-36 53.4-85.8 53.4-135.6 0-6.5-.6-13-1.9-18.2-50.5 1.9-110.7 33.7-147.4 75.3-28.4 32.4-55.1 82.2-55.1 132.6 0 7.1 1.3 14.2 1.9 16.5 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.9z"/>
                      </svg>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ color: '#aaa', fontSize: 9, letterSpacing: 0.5 }}>Disponível na</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>App Store</div>
                      </div>
                    </div>
                  </a>
                  {/* Android */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.fulltour.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: '#000', borderRadius: 10,
                      padding: '9px 18px', border: '1px solid #444',
                      minWidth: 145,
                    }}>
                      <svg width="22" height="22" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#4CAF50" d="M12 2.9 29.4 20 12 37.1A4 4 0 0 1 10 34V6a4 4 0 0 1 2-3.1z"/>
                        <path fill="#F44336" d="m32.7 22.9 4.5-2.6a2 2 0 0 0 0-3.5l-4.5-2.6L28 20l4.7 2.9z"/>
                        <path fill="#FFC107" d="m12 2.9 18.4 10.8L26 18.4 14.7 7.1A4 4 0 0 0 12 2.9z"/>
                        <path fill="#2196F3" d="m12 37.1 18.4-10.7-4.4-4.5L14.7 33A4 4 0 0 1 12 37.1z"/>
                      </svg>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ color: '#aaa', fontSize: 9, letterSpacing: 0.5 }}>Disponível no</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Google Play</div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              <Divider />

              {/* Rodapé final */}
              <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.8 }}>
                Este orçamento é pessoal e intransferível · Ref. {voucher.references}
                <br />
                Full Tour Technology · +56 9 6736 7782 · Marcelo Ferreira · Sistema v2.0.0
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default connect()(PresalePage);
